import { useState, useEffect, useMemo } from 'react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useSnapshot } from 'valtio'
import { configState, chatAction, chatState } from '@/store'
import { CircleArrowRight, X } from 'lucide-react'
import GuideBg from '@/assets/images/chat/guide_bg.svg'
import GuideArrow from '@/assets/images/chat/guide_arrow.svg'
import { pushEvent } from '@/api/chat'
import { useResponsive } from 'ahooks'

export default function GuideModal() {
  const [open, setOpen] = useState(false)
  const { bannerData } = useSnapshot(configState)
  const responsive = useResponsive()

  useEffect(() => {
    if (
      configState.bannerData?.dismiss_4_0_welcome ||
      configState.hasShowGuideModal
    )
      return
    if (
      configState.bannerData?.features?.thinking_w_images?.length > 0 &&
      responsive.sm
    ) {
      setOpen(true)
      configState.hasShowGuideModal = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bannerData])

  const cards = useMemo(
    () =>
      (configState.bannerData?.features?.thinking_w_images || []).slice(0, 3),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bannerData],
  )

  const handleClose = () => {
    setOpen(false)
  }

  const handleDontRemind = () => {
    pushEvent({
      action: 'dismiss_4_0_welcome',
    })
    setOpen(false)
  }

  const handleCardClick = (item: any) => {
    chatState.interactiveMode = true
    chatAction.onUserSubmit({
      text: item.text,
      pic: item.pic,
    })
    setOpen(false)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-full w-[736px] p-0 border-none shadow-none gap-0 [&>button]:hidden bg-gradient-to-b from-[#D5D3FF] to-[#F5F2F9] !rounded-[20px] overflow-hidden">
        <div className="absolute inset-0 z-10 select-none pointer-events-none">
          <img src={GuideBg} className="w-full h-full object-cover" alt="bg" />
        </div>

        <div
          className="absolute top-0 right-0 z-20 cursor-pointer"
          onClick={handleClose}
        >
          <div className="size-8 bg-black/5 rounded-se-[20px] rounded-es-[20px] flex items-center justify-center hover:bg-black/10 transition-colors">
            <X className="w-4 h-4 text-content-auxiliary" />
          </div>
        </div>

        <div className="relative z-10 p-8 pt-10">
          <div className="p-[10px] mb-6">
            <h2 className="text-4xl font-bold text-[#1C1F23] font-['FangYuanTiVF']">
              紫东太初4.0-VL重磅来袭
            </h2>
            <div className="relative mt-1">
              <h2 className="text-4xl font-bold text-[#7572FD] font-['FangYuanTiVF']">
                多模态理解与推理
              </h2>
              <img
                src={GuideArrow}
                className="absolute -bottom-[10px] left-[50px] select-none pointer-events-none"
                alt="arrow"
              />
            </div>
          </div>

          <div className="w-full bg-gradient-to-t from-[rgba(255,255,255,0.7)] to-white rounded-2xl px-6 py-8">
            <div className="text-sm font-medium text-content-first pb-5">
              赶快体验以下精彩案例吧~ 🎉
            </div>

            <div className="flex gap-3 items-center">
              {cards.map((item: any, index: number) => (
                <div
                  key={index}
                  className="flex-shrink-0 w-[200px] bg-[#F2F9FF] rounded-xl overflow-hidden cursor-pointer select-none hover:shadow-lg transition-shadow"
                  onClick={() => {
                    handleCardClick(item)
                  }}
                >
                  {/* Image Area */}
                  <div className="h-[120px] w-full flex items-center justify-center bg-gray-100 overflow-hidden">
                    <img
                      src={item.pic}
                      className="w-full h-full object-cover"
                      alt={item.title}
                    />
                  </div>
                  {/* Content Area */}
                  <div className="p-4 flex flex-col gap-2 h-[92px]">
                    <div className="font-medium text-sm text-content-primary line-clamp-1 text-[#1C1F23]">
                      {item.title}
                    </div>
                    <div className="text-xs text-[#77797B] line-clamp-2">
                      {item.text}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 mt-6">
            <Button variant="lightGray" onClick={handleDontRemind}>
              不再提醒
            </Button>
            <Button
              variant="rainbow"
              className="flex items-center gap-2"
              onClick={() => {
                chatState.interactiveMode = true
                handleClose()
              }}
            >
              体验更多
              <CircleArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
